import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router";
import { Box, Button, Skeleton, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useAuth } from "src/context/AuthContext";
import { listMyRequests, getCounts } from "src/utils/valut/valutApi";
import { captureError } from "src/utils/errorReporting";
import TilaaBackdrop from "src/components/quote/TilaaBackdrop";
import StepHeader from "src/components/quote/StepHeader";
import ValuListCard from "src/components/valut/ValuListCard";
import { amberCtaSx } from "src/components/quote/amberCtaSx";
import FrostedCard from "src/components/quote/FrostedCard";
import { useT } from "src/i18n";

// `statusesCsv` is the same CSV the tab passes to `?status=` — single source of
// truth. Each token becomes a `counts` key (matches the backend's wire shape
// from `GET /api/pumppuRequests/mine/counts`).
function sumCount(counts, statusesCsv) {
    return statusesCsv.split(",").reduce((acc, k) => acc + (counts?.[k] ?? 0), 0);
}

function emptyCopy(key, t) {
    switch (key) {
        case "avoimet": return t("valut.myValutPage.emptyOpen");
        case "hyvaksytyt": return t("valut.myValutPage.emptyAccepted");
        case "peruuntuneet": return t("valut.myValutPage.emptyCancelled");
        case "ei-alueellasi": return t("valut.myValutPage.emptyNoSupply");
        default: return "";
    }
}

export default function MyValut() {
    const { token, logout } = useAuth();
    const t = useT();
    const TAB_DEFS = useMemo(() => [
        { key: "avoimet",       label: t("valut.myValutPage.tabs.avoimet"),      statuses: "open,pending_verification" },
        { key: "hyvaksytyt",    label: t("valut.myValutPage.tabs.hyvaksytyt"),    statuses: "accepted" },
        { key: "peruuntuneet",  label: t("valut.myValutPage.tabs.peruuntuneet"),  statuses: "cancelled,expired" },
        { key: "ei-alueellasi", label: t("valut.myValutPage.tabs.eiAlueellasi"),  statuses: "no_supply" },
    ], [t]);
    const [params, setParams] = useSearchParams();
    const activeKey = TAB_DEFS.find((tab) => tab.key === params.get("tab"))?.key || "avoimet";
    const activeDef = TAB_DEFS.find((tab) => tab.key === activeKey);

    const [counts, setCounts] = useState(null);
    const [rows, setRows] = useState(null); // null = loading, [] = loaded-empty
    const [error, setError] = useState(null);

    const lastFetchRef = useRef(0);

    const fetchData = useCallback(async ({ silent = false } = {}) => {
        lastFetchRef.current = Date.now();
        if (!silent) {
            setRows(null);
            setError(null);
        }
        try {
            const [c, list] = await Promise.all([
                getCounts({ token }),
                listMyRequests({ token, status: activeDef.statuses }),
            ]);
            setCounts(c);
            setRows(list);
        } catch (err) {
            if (err.status === 401) { logout(); return; }
            // iOS fires visibilitychange before the network is back, so the
            // silent refetch's fetch throws "TypeError: Load failed" (Sentry
            // BETONIJERRY-1J). Keep the list on screen; the next focus retries.
            if (silent && err instanceof TypeError) return;
            captureError(err, { tags: { entity: "pumppuRequest", operation: "list_mine" } });
            setError(err);
            setRows([]);
        }
    }, [token, activeDef.statuses, logout]);

    useEffect(() => {
        document.title = t("valut.myValutPage.pageTitle");
        return () => { document.title = t("valut.myValutPage.pageTitleFallback"); };
    }, [t]);

    useEffect(() => {
        // Wrapped in an inner async fn so the react-hooks/set-state-in-effect
        // rule doesn't flag the synchronous setRows(null)/setError(null)
        // calls at the top of fetchData.
        async function run() {
            await fetchData();
        }
        run();
    }, [fetchData]);

    // Silent refetch on tab focus, debounced 5s.
    useEffect(() => {
        function onVisible() {
            if (document.visibilityState !== "visible") return;
            if (Date.now() - lastFetchRef.current < 5000) return;
            fetchData({ silent: true });
        }
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [fetchData]);

    function selectTab(_e, key) {
        const next = new URLSearchParams(params);
        next.set("tab", key);
        setParams(next, { replace: true });
    }

    return (
        <TilaaBackdrop>
            <StepHeader>{t("common.nav.myOrders")}</StepHeader>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                {t("valut.myValutPage.intro")}
            </Typography>

            <Tabs
                value={activeKey}
                onChange={selectTab}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{ mb: 3 }}
            >
                {TAB_DEFS.map((tab) => (
                    <Tab
                        key={tab.key}
                        value={tab.key}
                        label={`${tab.label} (${sumCount(counts, tab.statuses)})`}
                    />
                ))}
            </Tabs>

            {activeKey === "ei-alueellasi" && rows && rows.length > 0 && (
                <FrostedCard sx={{ p: 2, mb: 2 }}>
                    <Typography variant="body2">
                        {t("valut.myValutPage.noSupplyNotice")}
                    </Typography>
                </FrostedCard>
            )}

            <Stack spacing={2}>
                {rows === null && (
                    <Box
                        role="status"
                        aria-label={t("valut.myValutPage.loadingAriaLabel")}
                        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                        {[0, 1, 2].map((i) => (
                            <FrostedCard key={`sk-${i}`} aria-hidden sx={{ p: 2 }}>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: "stretch" }}>
                                    <Skeleton
                                        variant="rounded"
                                        sx={{ width: { xs: "100%", sm: 120 }, height: { xs: 140, sm: 80 }, flexShrink: 0 }}
                                    />
                                    <Stack spacing={0.75} sx={{ flex: 1, justifyContent: "center" }}>
                                        <Skeleton variant="text" width="55%" height={26} />
                                        <Skeleton variant="text" width="80%" />
                                        <Skeleton variant="text" width="35%" />
                                    </Stack>
                                    <Skeleton
                                        variant="rounded"
                                        width={88}
                                        height={34}
                                        sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
                                    />
                                </Stack>
                            </FrostedCard>
                        ))}
                    </Box>
                )}
                {rows !== null && rows.length === 0 && !error && (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {emptyCopy(activeKey, t)}
                    </Typography>
                )}
                {error && (
                    <Typography variant="body2" color="error">
                        {t("valut.myValutPage.loadFailed")}
                    </Typography>
                )}
                {rows && rows.map((row, i) => (
                    <ValuListCard
                        key={row.pumppuRequestId}
                        row={row}
                        index={i}
                        hideStatusChip={activeKey === "avoimet"}
                        hideOfferPill={activeKey === "peruuntuneet" || activeKey === "ei-alueellasi"}
                    />
                ))}
            </Stack>

            <Box sx={{ textAlign: "center", mt: 4 }}>
                <Button
                    component={RouterLink}
                    to="/tilaa"
                    variant="contained"
                    sx={amberCtaSx}
                    size="large"
                >
                    {t("valut.shared.newRequestCta")}
                </Button>
            </Box>
        </TilaaBackdrop>
    );
}
